import Box from "@mui/material/Box"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { makeStyles } from "tss-react/mui-compat"
import Autocomplete from "components/Autocomplete"
import CheckboxGroup from "components/CheckboxGroup"
import {
  exhibitorTypeOptions,
  schoolLevelOptions,
  schoolControlOptions,
  schoolSizeOptions,
} from "components/use-query-params-filters"
import { api } from "lib/api"
import { Program } from "lib/types"
import useCurrent from "lib/use-current"
import { useEffect, useState } from "react"
import { usaStates } from "typed-usa-states"
import { useDebouncedCallback } from "use-debounce"

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(1),
    },
    autocompleteRoot: {
      ["& .makeStyles-root-9"]: {
        marginBottom: 0,
      },
    },
  })
)

const allStatesByName = usaStates.map((state) => state.name)

interface ICommonFilters {
  action: (payload: any) => void
  state: any
}

const CommonFilters = (props: ICommonFilters) => {
  const { action, state } = props

  const [loading, setLoading] = useState(false)
  const [term, setTerm] = useState("")
  const [majors, setMajors] = useState<Array<{ name: string; id: string }>>([])

  const debouncedSearch = useDebouncedCallback(async () => {
    setLoading(true)
    const { data: programs } = await api.searchPrograms({ req: undefined }, term)
    const results = programs.map((program: Program) => {
      return {
        id: program.id,
        name: program.attributes.name,
      }
    })
    setMajors(results)
    setLoading(false)
  }, 250)

  useEffect(() => {
    debouncedSearch.callback()
  }, [term])

  const { classes } = useStyles()

  // @ts-ignore
  const { config: providerConfig } = useCurrent()

  return (
    <>
      <Box className={classes.root}>
        <Typography variant="body1" color="textSecondary">
          Locations
        </Typography>
        <Autocomplete
          multiple
          options={allStatesByName}
          getOptionLabel={(option) => option}
          classes={{
            root: classes.autocompleteRoot,
          }}
          value={state.states}
          onChange={(_, data) =>
            action({
              states: data,
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Search by State"
              margin="none"
              InputProps={{ ...params.InputProps }}
            />
          )}
        />
      </Box>

      <Box className={classes.root}>
        <FormControlLabel
          control={
            <Checkbox
              checked={state.international}
              onChange={(event) =>
                action({
                  international: event.target.checked,
                })
              }
              name="International"
              color="primary"
            />
          }
          label="Show International Locations"
        />
      </Box>

      <Box className={classes.root}>
        <Typography variant="body1" color="textSecondary">
          Majors
        </Typography>
        <Autocomplete
          multiple
          loading={loading}
          options={majors}
          getOptionLabel={(option) => option.name}
          classes={{
            root: classes.autocompleteRoot,
          }}
          value={state.majors}
          onChange={(_, data) =>
            action({
              majors: data,
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Search Majors"
              margin="normal"
              InputProps={{ ...params.InputProps }}
              onChange={(e) => setTerm(e.target.value)}
            />
          )}
        />
      </Box>

      <CheckboxGroup
        checked={state.school_control}
        label={`Type of ${providerConfig.exhibitor_copy_text}`}
        options={schoolControlOptions}
        onChange={(values) => {
          action({
            school_control: values,
          })
        }}
      />

      <CheckboxGroup
        checked={state.school_level}
        label="Type of Program"
        options={schoolLevelOptions}
        onChange={(values) => {
          action({
            school_level: values,
          })
        }}
      />

      <CheckboxGroup
        checked={state.school_size}
        label={`Size of ${providerConfig.exhibitor_copy_text}`}
        options={schoolSizeOptions}
        onChange={(values) => {
          action({
            school_size: values,
          })
        }}
      />

      <CheckboxGroup
        checked={state.exhibitor_type}
        label="Type of Exhibitor"
        options={exhibitorTypeOptions}
        onChange={(values) => {
          action({
            exhibitor_type: values,
          })
        }}
      />
    </>
  )
}

export default CommonFilters
